import { defineStore } from 'pinia';
import { ref, computed, toRaw } from 'vue';
import {
    fetchTickets,
    fetchTicketDetails,
    createTicket,
    addTicketReply,
    acknowledgeTicketAttentionRequest,
    createCollaborationInternalNote,
    fetchDashboardStats,
    changeTicketStatus,
    changeTicketPriority,
    assignTicket,
    closeTicket,
    handoffTicket,
    reopenTicket,
    resolveTicketAttentionRequest,
    searchTickets,
    fetchSupportUserProfile,
    fetchSupportAgents,
    fetchCannedResponses,
    useCannedResponse,
    fetchReplyTemplates,
    fetchTicketCategories,
    updateTicketWatchers,
    buildSupportAttachmentPayload
} from '@/service/api/support';
import { $t } from '@/locales';
import { useRouteStore } from '@/store/modules/route';
import { useUserStore } from '@/store/modules/user';
import { useAuthStore } from '@/store/modules/auth';
import {
    type SupportProfileCommitGuard,
    runGuardedSupportProfileCommit
} from '@/utils/support-customer-ai';
import { sanitizeSupportError } from '@/utils/support-errors';
import { createSupportProfileRequestCoordinator } from '@/utils/support-profile-request';
import { mergeSupportTicketDetail, mergeSupportTicketListItem } from '@/utils/support-ticket-preview';
import {
    hasSupportAICustomerReadinessAccess as hasCustomerReadinessCapability,
    hasSupportAICalibrationApproveAccess as hasCalibrationApproveCapability,
    hasSupportAICalibrationManageAccess as hasCalibrationManageCapability,
    hasSupportAICalibrationReviewAccess as hasCalibrationReviewCapability,
    hasSupportAIOperationsAccess as hasOperationsCapability,
    hasSupportAIReleaseRequestAccess as hasReleaseRequestCapability,
    hasSupportAIReplayExecutionAccess,
    hasSupportAIRolloutControlAccess as hasRolloutControlCapability
} from '@/utils/support-ai-governance';

// Throttle the "please log in" toast so the many authenticated support calls
// that fire in parallel on a page (tickets + dashboard stats + profile, etc.)
// surface at most one message per interval instead of stacking duplicates.
let lastNeedLoginToastAt = 0;
const NEED_LOGIN_TOAST_INTERVAL_MS = 3000;
// Polling synchronization types
interface PollingUpdate {
    type: 'tickets' | 'ticket-details' | 'dashboard-stats';
    data: any;
    timestamp: number;
    source?: string;
}

interface PollingState {
    lastUpdate: number;
    isPolling: boolean;
    pollingInstances: Set<string>;
    updateQueue: PollingUpdate[];
}

export const useSupportStore = defineStore('support', () => {
    // State
    const tickets = ref<Api.Support.Ticket[]>([]);
    const currentTicket = ref<Api.Support.Ticket | null>(null);
    const dashboardStats = ref<Api.Support.DashboardStats | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const errorCode = ref<number | null>(null); // HTTP status code for errors

    // User profile and roles state
    const userProfile = ref<Api.Support.User | null>(null);
    const userProfileLoading = ref(false);
    const userProfileLoaded = ref(false);
    const userProfileError = ref<string | null>(null);
    const userProfileRequestCoordinator = createSupportProfileRequestCoordinator<Api.Support.User>();
    const dashboardStatsError = ref<string | null>(null);

    // Agent-related state
    const agents = ref<Api.Support.User[]>([]);
    const agentsLoading = ref(false);
    const agentsLoaded = ref(false);

    // Canned responses state
    const cannedResponses = ref<Api.Support.CannedResponse[]>([]);
    const cannedResponsesLoading = ref(false);
    const cannedResponsesLoaded = ref(false);

    // Agent reply templates state (backend-served, agent-only)
    const replyTemplates = ref<Api.Support.ReplyTemplates | null>(null);
    const replyTemplatesLoading = ref(false);
    const replyTemplatesLoaded = ref(false);

    // Ticket categories state. Categories are effectively static reference data
    // that multiple views (dashboard, create-ticket, detail) all need, so we
    // cache the backend list once and dedupe concurrent in-flight requests.
    const categories = ref<Api.Support.TicketCategory[]>([]);
    const categoriesLoading = ref(false);
    const categoriesLoaded = ref(false);
    let categoriesRequest: Promise<Api.Support.TicketCategory[]> | null = null;

    // NOTE: User addresses are intentionally NOT cached in this store.
    // Addresses are PII and can be edited by the user at any time, so each
    // consumer (ticket form, ticket detail) fetches them locally on demand and
    // discards them when unmounted rather than persisting them in shared state.

    // Polling synchronization state
    const pollingState = ref<PollingState>({
        lastUpdate: 0,
        isPolling: false,
        pollingInstances: new Set(),
        updateQueue: []
    });

    // Filters and pagination
    const filters = ref<Api.Support.TicketFilters>({});
    const pagination = ref<Api.Support.PaginationData>({
        page: 1,
        pageSize: 20,
        total: 0
    });

    // Search
    const searchQuery = ref('');
    const searchResults = ref<Api.Support.Ticket[]>([]);
    const searching = ref(false);

    // Computed
    const openTickets = computed(() =>
        tickets.value.filter(ticket => ticket.status === 'open')
    );

    const inProgressTickets = computed(() =>
        tickets.value.filter(ticket => ticket.status === 'in_progress')
    );

    const resolvedTickets = computed(() =>
        tickets.value.filter(ticket => ticket.status === 'resolved')
    );

    const overdueTickets = computed(() =>
        tickets.value.filter(ticket => ticket.is_overdue)
    );

    const myTickets = computed(() =>
        tickets.value.filter(ticket => ticket.created_by.id === currentUserId.value)
    );

    const currentUserId = ref<string | null>(null);

    const filteredTickets = computed(() => {
        let result = tickets.value;

        // Apply search filter
        if (searchQuery.value) {
            const query = searchQuery.value.toLowerCase();
            result = result.filter(ticket =>
                ticket.subject.toLowerCase().includes(query) ||
                ticket.ticket_number.toLowerCase().includes(query) ||
                (ticket.description ?? '').toLowerCase().includes(query)
            );
        }

        // Apply status filter
        if (filters.value.status && filters.value.status !== 'all') {
            result = result.filter(ticket => ticket.status === filters.value.status);
        }

        // Apply priority filter
        if (filters.value.priority && filters.value.priority.length > 0) {
            result = result.filter(ticket => filters.value.priority!.includes(ticket.priority));
        }

        // Apply category filter
        if (filters.value.category && filters.value.category.length > 0) {
            result = result.filter(ticket => {
                const categoryId = ticket.category?.id;
                return categoryId !== undefined && filters.value.category!.includes(categoryId);
            });
        }

        return result;
    });

    // Role-based computed properties
    const isAgent = computed(() => userProfile.value?.is_support_agent || false);
    const isAdmin = computed(() => userProfile.value?.is_support_admin || false);
    const hasAgentAccess = computed(() => isAgent.value || isAdmin.value);
    const hasSupportAccess = computed(() => userProfile.value?.has_support_access || false);
    const hasSupportAIOperationsAccess = computed(
        () =>
            !userProfileLoading.value &&
            !userProfileError.value &&
            hasOperationsCapability(userProfile.value?.support_ai_governance)
    );
    const hasSupportAIReplayAccess = computed(
        () =>
            hasSupportAIOperationsAccess.value &&
            hasSupportAIReplayExecutionAccess(userProfile.value?.support_ai_governance)
    );
    const hasSupportAIReleaseRequestAccess = computed(
        () =>
            hasSupportAIOperationsAccess.value &&
            hasReleaseRequestCapability(toRaw(userProfile.value?.support_ai_governance))
    );
    const hasSupportAICustomerReadinessAccess = computed(
        () =>
            hasSupportAIOperationsAccess.value &&
            hasCustomerReadinessCapability(userProfile.value?.support_ai_governance)
    );
    const hasSupportAIRolloutControlAccess = computed(
        () =>
            hasSupportAIOperationsAccess.value &&
            hasRolloutControlCapability(userProfile.value?.support_ai_governance)
    );
    const hasSupportAICalibrationReviewAccess = computed(
        () => hasSupportAIOperationsAccess.value && hasCalibrationReviewCapability(userProfile.value?.support_ai_governance)
    );
    const hasSupportAICalibrationManageAccess = computed(
        () => hasSupportAIOperationsAccess.value && hasCalibrationManageCapability(userProfile.value?.support_ai_governance)
    );
    const hasSupportAICalibrationApproveAccess = computed(
        () => hasSupportAIOperationsAccess.value && hasCalibrationApproveCapability(userProfile.value?.support_ai_governance)
    );

    // Tickets assigned to the current user (for agent dashboard)
    const assignedToMeTickets = computed(() => {
        if (!userProfile.value) return [];
        return tickets.value.filter(ticket => ticket.assigned_to?.id === userProfile.value?.id);
    });

    // Unassigned tickets (for agent dashboard)
    const unassignedTickets = computed(() =>
        tickets.value.filter(ticket => !ticket.assigned_to && ticket.status !== 'closed')
    );

    const logSupportError = (message: string, source: unknown) => {
        console.error(message, sanitizeSupportError(source));
    };

    /**
     * Central authentication gate for every support API call that needs a
     * logged-in user. Support views are guest-viewable, so a logged-out visitor
     * can reach them; short-circuiting here (before any network request) avoids
     * confusing 401/403 "permission denied — contact your administrator" states
     * and instead nudges the user to log in.
     *
     * The knowledge base is intentionally NOT gated: its API calls are made
     * directly from the view layer (not through this store), so they remain
     * publicly accessible to guests.
     *
     * @param options.silent Suppress the toast (used by background polling).
     * @returns `true` when authenticated and the caller may proceed.
     */
    const requireAuth = (options: { silent?: boolean } = {}): boolean => {
        const userStore = useUserStore();
        const authStore = useAuthStore();

        // A user is considered authenticated if EITHER:
        //  - the async user profile flag `isAuthenticated` is set (populated by
        //    the ping / getUserInfo response), OR
        //  - a valid auth token exists (`authStore.isLogin` reads the token from
        //    localStorage synchronously at boot).
        //
        // The token check is essential: on a fresh page load / refresh the
        // support view's `onMounted` fires its API calls BEFORE the async ping
        // resolves and sets `isAuthenticated`, so relying only on that flag made
        // logged-in users look like guests and blocked every support component.
        if (userStore.isUserAuthenticated() || authStore.isLogin) return true;

        if (!options.silent) {
            const now = Date.now();
            if (now - lastNeedLoginToastAt > NEED_LOGIN_TOAST_INTERVAL_MS) {
                lastNeedLoginToastAt = now;
                window.$message?.error?.($t('page.login.common.needLogin'));
            }
        }

        return false;
    };

    /**
     * Update navigation menus based on support roles
     * This shows/hides agent-only menu items like "All Tickets"
     */
    const updateMenusForSupportRoles = (hasAccess: boolean, hasOperationsAccess = false) => {
        try {
            const routeStore = useRouteStore();
            routeStore.updateMenusByPermission({
                hasAgentAccess: hasAccess,
                hasSupportAIOperationsAccess: hasOperationsAccess
            });
        } catch (err) {
            logSupportError('Failed to update menus for support roles:', err);
        }
    };

    const invalidateSupportAIOperationsAccess = () => {
        if (userProfile.value) {
            const profile = { ...userProfile.value };
            delete profile.support_ai_governance;
            userProfile.value = profile;
        }
        updateMenusForSupportRoles(hasAgentAccess.value, false);
    };

    // Actions - User Profile and Roles
    const getUserProfile = async (
        force = false,
        commitGuard: SupportProfileCommitGuard = () => true
    ) => {
        // Guests have no profile to fetch; skip silently (no toast) since this
        // is used internally by guards/menu logic, not a user-initiated action.
        if (!requireAuth({ silent: true })) {
            return null;
        }

        // Return cached profile if already loaded and not forcing refresh
        if (userProfileLoaded.value && !force) {
            return userProfile.value;
        }

        return userProfileRequestCoordinator.run({
            force,
            commitGuard,
            onStart: () => {
                userProfileLoading.value = true;
                userProfileError.value = null;
                updateMenusForSupportRoles(hasAgentAccess.value, false);
            },
            onFinish: () => {
                userProfileLoading.value = false;
            },
            execute: async requestCommitGuard => {
                try {
                    const { data, error: apiError } = await fetchSupportUserProfile();
                    if (!runGuardedSupportProfileCommit(requestCommitGuard, () => {})) return null;

                    if (apiError) {
                        logSupportError('Failed to fetch user profile:', apiError);
                        runGuardedSupportProfileCommit(requestCommitGuard, () => {
                            userProfileError.value = $t('support.messages.profileUnavailable');
                            updateMenusForSupportRoles(false, false);
                        });
                        return null;
                    }

                    if (data) {
                        const committed = runGuardedSupportProfileCommit(requestCommitGuard, () => {
                            userProfile.value = data;
                            userProfileLoaded.value = true;
                            userProfileError.value = null;

                            const hasAccess = Boolean(data.is_support_agent) || Boolean(data.is_support_admin);
                            updateMenusForSupportRoles(
                                hasAccess,
                                hasOperationsCapability(data.support_ai_governance)
                            );
                        });
                        return committed ? data : null;
                    }
                } catch (err) {
                    if (!runGuardedSupportProfileCommit(requestCommitGuard, () => {})) return null;
                    logSupportError('Failed to fetch user profile:', err);
                    runGuardedSupportProfileCommit(requestCommitGuard, () => {
                        userProfileError.value = $t('support.messages.profileUnavailable');
                        updateMenusForSupportRoles(false, false);
                    });
                }
                return null;
            }
        });
    };

    const recheckUserProfilePreservingCachedAccess = async (
        commitGuard: SupportProfileCommitGuard = () => true
    ) => {
        const commitRevoked = (requestCommitGuard: SupportProfileCommitGuard) => {
            let committed = false;
            runGuardedSupportProfileCommit(requestCommitGuard, () => {
                invalidateSupportAIOperationsAccess();
                committed = true;
            });
            return committed;
        };
        return userProfileRequestCoordinator.run({
            force: true,
            commitGuard,
            onStart: () => {
                userProfileLoading.value = false;
                updateMenusForSupportRoles(
                    hasAgentAccess.value,
                    hasOperationsCapability(userProfile.value?.support_ai_governance)
                );
            },
            onFinish: () => {
                userProfileLoading.value = false;
            },
            execute: async requestCommitGuard => {
                try {
                    const { data, error: apiError } = await fetchSupportUserProfile();
                    if (!runGuardedSupportProfileCommit(requestCommitGuard, () => {})) return null;
                    if (apiError) {
                        if (sanitizeSupportError(apiError).kind === 'permission') {
                            commitRevoked(requestCommitGuard);
                        }
                        return null;
                    }
                    if (!data) return null;

                    const committed = runGuardedSupportProfileCommit(requestCommitGuard, () => {
                        userProfile.value = data;
                        userProfileLoaded.value = true;
                        userProfileError.value = null;
                        const hasAccess = Boolean(data.is_support_agent) || Boolean(data.is_support_admin);
                        updateMenusForSupportRoles(
                            hasAccess,
                            hasOperationsCapability(data.support_ai_governance)
                        );
                    });
                    return committed ? data : null;
                } catch (error) {
                    if (!runGuardedSupportProfileCommit(requestCommitGuard, () => {})) return null;
                    if (sanitizeSupportError(error).kind === 'permission') {
                        commitRevoked(requestCommitGuard);
                    }
                    return null;
                }
            }
        });
    };

    const recheckStaffAIAccess = async (
        commitGuard: SupportProfileCommitGuard = () => true
    ): Promise<'retained' | 'revoked' | 'unknown' | 'stale'> => {
        const profile = await recheckUserProfilePreservingCachedAccess(commitGuard);
        if (!commitGuard()) return 'stale';
        if (!profile) return hasAgentAccess.value ? 'unknown' : 'revoked';
        return hasAgentAccess.value ? 'retained' : 'revoked';
    };

    // Fetch support agents list (for assignment dropdown)
    const getAgents = async (force = false) => {
        // Guests can never be agents; bail silently before any profile/API work.
        if (!requireAuth({ silent: true })) {
            return [];
        }

        // Only agents/admins can fetch the agents list
        // Check if user profile is loaded first, if not load it
        if (!userProfile.value && !userProfileLoading.value) {
            await getUserProfile();
        }
        
        // If still no agent access, don't make the API call
        if (!hasAgentAccess.value) {
            console.warn('[SupportStore] getAgents called by non-agent user, skipping API call');
            return [];
        }
        

        // Return cached agents if already loaded and not forcing refresh
        if (agentsLoaded.value && !force) {
            return agents.value;
        }

        agentsLoading.value = true;
        try {
            const { data, error: apiError } = await fetchSupportAgents();

            if (apiError) {
                logSupportError('Failed to fetch agents:', apiError);
                return [];
            }

            if (data) {
                agents.value = data;
                agentsLoaded.value = true;
                return data;
            }
        } catch (err) {
            logSupportError('Failed to fetch agents:', err);
        } finally {
            agentsLoading.value = false;
        }
        return [];
    };

    // Fetch canned responses (for agents)
    const getCannedResponses = async (force = false) => {
        // Guests can never be agents; bail silently before any profile/API work.
        if (!requireAuth({ silent: true })) {
            return [];
        }

        // Only agents/admins can fetch canned responses
        // Check if user profile is loaded first, if not load it
        if (!userProfile.value && !userProfileLoading.value) {
            await getUserProfile();
        }
        
        // If still no agent access, don't make the API call
        if (!hasAgentAccess.value) {
            console.warn('[SupportStore] getCannedResponses called by non-agent user, skipping API call');
            return [];
        }

        // Return cached responses if already loaded and not forcing refresh
        if (cannedResponsesLoaded.value && !force) {
            return cannedResponses.value;
        }

        cannedResponsesLoading.value = true;
        try {
            const { data, error: apiError } = await fetchCannedResponses();

            if (apiError) {
                logSupportError('Failed to fetch canned responses:', apiError);
                return [];
            }

            if (data) {
                cannedResponses.value = data;
                cannedResponsesLoaded.value = true;
                return data;
            }
        } catch (err) {
            logSupportError('Failed to fetch canned responses:', err);
        } finally {
            cannedResponsesLoading.value = false;
        }
        return [];
    };

    // Track only an explicit, successful insertion. Selection and preview never
    // call this action. Tracking failures are non-fatal to the reply workflow.
    const recordCannedResponseUse = async (responseId: number) => {
        if (!hasAgentAccess.value) return false;

        try {
            const { data, error: apiError } = await useCannedResponse(responseId);
            if (apiError) {
                logSupportError('Failed to track canned response use:', apiError);
                return false;
            }

            const index = cannedResponses.value.findIndex(response => response.id === responseId);
            if (index >= 0) {
                const current = cannedResponses.value[index];
                cannedResponses.value[index] = data || {
                    ...current,
                    usage_count: Number(current.usage_count || 0) + 1
                };
            }
            return true;
        } catch (err) {
            logSupportError('Failed to track canned response use:', err);
            return false;
        }
    };

    // Fetch agent reply templates (backend-served; no frontend fallback so
    // template copy never ships in the public bundle). Agents can force a
    // refresh from the reply form.
    const getReplyTemplates = async (force = false) => {
        // Guests can never be agents; bail silently before any profile/API work.
        if (!requireAuth({ silent: true })) {
            return null;
        }

        // Only agents/admins can fetch reply templates
        if (!userProfile.value && !userProfileLoading.value) {
            await getUserProfile();
        }

        if (!hasAgentAccess.value) {
            console.warn('[SupportStore] getReplyTemplates called by non-agent user, skipping API call');
            return null;
        }

        // Return cached templates if already loaded and not forcing refresh
        if (replyTemplatesLoaded.value && !force) {
            return replyTemplates.value;
        }

        replyTemplatesLoading.value = true;
        try {
            const { data, error: apiError } = await fetchReplyTemplates();

            if (apiError) {
                logSupportError('Failed to fetch reply templates:', apiError);
                return null;
            }

            if (data?.results) {
                replyTemplates.value = data.results;
                replyTemplatesLoaded.value = true;
                return data.results;
            }
        } catch (err) {
            logSupportError('Failed to fetch reply templates:', err);
        } finally {
            replyTemplatesLoading.value = false;
        }
        return null;
    };

    // Fetch ticket categories once and cache them. Public reference data, so no
    // auth gate. Concurrent callers (e.g. dashboard + a mounted create form)
    // share a single in-flight request via `categoriesRequest`.
    const getCategories = async (force = false): Promise<Api.Support.TicketCategory[]> => {
        if (categoriesLoaded.value && !force) {
            return categories.value;
        }

        if (categoriesRequest) {
            return categoriesRequest;
        }

        categoriesLoading.value = true;
        categoriesRequest = (async () => {
            try {
                const { data, error: apiError } = await fetchTicketCategories();

                if (apiError) {
                    logSupportError('Failed to fetch ticket categories:', apiError);
                    return categories.value;
                }

                if (data) {
                    categories.value = data.slice().sort((a, b) => a.id - b.id);
                    categoriesLoaded.value = true;
                }

                return categories.value;
            } catch (err) {
                logSupportError('Failed to fetch ticket categories:', err);
                return categories.value;
            } finally {
                categoriesLoading.value = false;
                categoriesRequest = null;
            }
        })();

        return categoriesRequest;
    };

    // Actions - Ticket Management
    const getTickets = async (params?: Api.Support.TicketQueryParams) => {
        // Authenticated-only: guests get a throttled "please log in" toast and
        // an empty (non-error) state instead of a confusing permission error.
        if (!requireAuth()) {
            tickets.value = [];
            pagination.value.total = 0;
            loading.value = false;
            error.value = null;
            errorCode.value = null;
            return;
        }

        loading.value = true;
        error.value = null;
        errorCode.value = null;

        try {
            const { data, error: apiError, statusCode } = await fetchTickets({
                ...filters.value,
                ...params,
                page: pagination.value.page,
                page_size: pagination.value.pageSize
            });

            if (apiError) {
                // Check for 429 rate limit using statusCode
                if (statusCode === 429) {
                    error.value = $t("support.polling.tooManyRequests");
                    errorCode.value = 429;
                    console.warn('[SupportStore] Rate limit error for tickets list (apiError)');
                } else {
                    error.value = $t("common.fetchError");
                    errorCode.value = statusCode || null;
                    logSupportError('Failed to fetch tickets:', apiError);
                }
                return;
            }

            if (data) {
                tickets.value = (data.results || []).map(ticket => mergeSupportTicketListItem(
                    tickets.value.find(existing => existing.ticket_number === ticket.ticket_number), ticket, hasAgentAccess.value
                ));
                pagination.value.total = data.count || 0;
            } else {
                // No data received, set empty state
                tickets.value = [];
                pagination.value.total = 0;
            }
        } catch (err: any) {
            // Check for 429 rate limit errors using statusCode or response.status
            const errStatusCode = err?.statusCode || err?.response?.status;
            if (errStatusCode === 429) {
                error.value = $t("support.polling.tooManyRequests");
                errorCode.value = 429;
                console.warn('[SupportStore] Rate limit error for tickets list');
            } else {
                error.value = $t("common.fetchError");
                errorCode.value = errStatusCode || null;
                logSupportError('Failed to fetch tickets:', err);
            }
        } finally {
            loading.value = false;
        }
    };

    const getTicketDetails = async (ticketNumber: string) => {
        // Authenticated-only: guests get a throttled "please log in" toast and
        // no ticket (the LoginOverlay already covers the view). Returning null
        // instead of throwing avoids the misleading permission error state.
        if (!requireAuth()) {
            loading.value = false;
            error.value = null;
            errorCode.value = null;
            return null;
        }

        loading.value = true;
        error.value = null;
        errorCode.value = null;

        try {
            const { data: ticket, error: apiError, statusCode } = await fetchTicketDetails(ticketNumber);

            if (apiError) {
                // Check for 429 rate limit using statusCode
                if (statusCode === 429) {
                    error.value = $t("support.polling.tooManyRequests");
                    errorCode.value = 429;
                    console.warn('[SupportStore] Rate limit error for ticket details (apiError)');
                } else {
                    error.value = $t("common.fetchError");
                    errorCode.value = statusCode || null;
                    logSupportError('Failed to fetch ticket details:', apiError);
                }
                // Attach statusCode to the error for component access
                (apiError as any).statusCode = statusCode;
                throw apiError;
            }

            if (ticket) {
                // Smart merge: only update if replies changed to prevent duplicates
                if (currentTicket.value?.ticket_number === ticketNumber) {
                    // Merge replies by created_at to avoid duplicates
                    const mergedReplies = mergeRepliesByCreatedAt(currentTicket.value.replies, ticket.replies);
                    currentTicket.value = {
                        ...ticket,
                        replies: mergedReplies
                    };
                } else {
                    currentTicket.value = ticket;
                }

                // Update ticket in list if it exists.
                // IMPORTANT: merge instead of replace — the detail endpoint
                // does not include list-only fields. A newer list reply may
                // have arrived during this read; only covered replies are read.
                const existingIndex = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
                if (existingIndex !== -1) {
                    const existingListTicket = tickets.value[existingIndex];
                    tickets.value[existingIndex] = mergeSupportTicketDetail(existingListTicket, ticket, hasAgentAccess.value);
                }

                return ticket;
            }
        } catch (err: any) {
            // Check for 429 rate limit errors using statusCode or response.status
            const errStatusCode = err?.statusCode || err?.response?.status;
            if (errStatusCode === 429) {
                error.value = $t("support.polling.tooManyRequests");
                errorCode.value = 429;
                console.warn('[SupportStore] Rate limit error for ticket details');
            } else {
                error.value = $t("common.fetchError");
                errorCode.value = errStatusCode || null;
                logSupportError('getTicketDetails Failed to fetch ticket details:', err);
            }
            throw err;
        } finally {
            loading.value = false;

        }
    };

    /**
     * Merge two arrays of replies by created_at timestamp to prevent duplicates
     * Keeps the most recent version of each reply
     */
    const mergeRepliesByCreatedAt = (existingReplies: Api.Support.TicketReply[], newReplies: Api.Support.TicketReply[]): Api.Support.TicketReply[] => {
        const replyMap = new Map<string, Api.Support.TicketReply>();

        // Add existing replies to map using created_at as key
        existingReplies.forEach(reply => {
            replyMap.set(reply.created_at, reply);
        });

        // Update/add new replies (newer data takes precedence)
        newReplies.forEach(reply => {
            replyMap.set(reply.created_at, reply);
        });

        // Convert map back to array and sort by creation date
        return Array.from(replyMap.values()).sort((a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
    };

    /**
     * Safely merge a partial ticket response with existing ticket data.
     * This prevents undefined errors when API returns partial data (e.g., missing replies, attachments).
     * The partial response's scalar values take precedence, but nested arrays are preserved if missing.
     */
    const mergeTicketWithExisting = (
        existingTicket: Api.Support.Ticket,
        partialTicket: Api.Support.TicketActionResponse
    ): Api.Support.Ticket => {
        // Guard: if partialTicket has category/created_by as explicit null, use existing
        // The spread will override with partial values, then we explicitly restore nested objects
        const merged = {
            ...existingTicket,
            ...partialTicket,
            // Preserve nested arrays if not present in partial response (ensure non-null)
            replies: partialTicket.replies ?? existingTicket.replies ?? [],
            attachments: partialTicket.attachments ?? existingTicket.attachments ?? [],
            custom_fields: partialTicket.custom_fields ?? existingTicket.custom_fields ?? [],
            collaboration_summary: partialTicket.collaboration_summary ?? existingTicket.collaboration_summary,
            // Preserve nested objects - use nullish coalescing to handle both null and undefined
            category: partialTicket.category ?? existingTicket.category,
            created_by: partialTicket.created_by ?? existingTicket.created_by,
            // For assigned_to and address, explicit null means "unassigned", so check differently
            assigned_to: partialTicket.assigned_to !== undefined ? partialTicket.assigned_to : existingTicket.assigned_to,
            address: partialTicket.address !== undefined ? partialTicket.address : existingTicket.address,
            // Preserve status if undefined in partial response (prevent 'support.status.undefined' error)
            status: partialTicket.status ?? existingTicket.status,
            priority: partialTicket.priority ?? existingTicket.priority
        };
        return merged;
    };

    const createNewTicket = async (data: Api.Support.CreateTicketData) => {
        // Authenticated-only user action.
        if (!requireAuth()) {
            return undefined;
        }

        loading.value = true;
        error.value = null;

        try {
            const payload = buildSupportAttachmentPayload(data, data.attachment_files || []);
            const { data: newTicket, error: apiError } = await createTicket(payload);

            if (apiError) {
                error.value = $t('support.messages.errorCreatingTicket');
                logSupportError('Failed to create ticket:', apiError);
                throw apiError;
            }

            if (newTicket) {
                tickets.value.unshift(newTicket);
                pagination.value.total += 1;
                return newTicket;
            }
        } catch (err) {
            error.value = $t('support.messages.errorCreatingTicket');
            logSupportError('Failed to create ticket:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const mergeCollaborationMutation = (
        ticketNumber: string,
        response: Api.Support.TicketCollaborationMutationResponse
    ) => {
        const ticketFromResponse = response.ticket;
        const replyFromResponse = response.reply;

        if (ticketFromResponse) {
            if (currentTicket.value?.ticket_number === ticketNumber) {
                currentTicket.value = mergeTicketWithExisting(currentTicket.value, ticketFromResponse);
            }

            const index = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
            if (index !== -1) {
                tickets.value[index] = mergeTicketWithExisting(tickets.value[index], ticketFromResponse);
            }
        }

        if (response.collaboration_summary) {
            if (currentTicket.value?.ticket_number === ticketNumber) {
                currentTicket.value = {
                    ...currentTicket.value,
                    collaboration_summary: response.collaboration_summary
                };
            }

            const index = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
            if (index !== -1) {
                tickets.value[index] = {
                    ...tickets.value[index],
                    collaboration_summary: response.collaboration_summary
                };
            }
        }

        if (replyFromResponse && currentTicket.value?.ticket_number === ticketNumber) {
            const existingReplyIndex = currentTicket.value.replies.findIndex(r => r.id === replyFromResponse.id || r.created_at === replyFromResponse.created_at);
            if (existingReplyIndex === -1) {
                currentTicket.value.replies.push(replyFromResponse);
            } else {
                currentTicket.value.replies[existingReplyIndex] = replyFromResponse;
            }
        }

        return currentTicket.value?.ticket_number === ticketNumber
            ? currentTicket.value
            : tickets.value.find(t => t.ticket_number === ticketNumber);
    };

    const addReply = async (
        ticketNumber: string,
        replyData: Api.Support.CreateReplyData,
        isCurrent: () => boolean = () => true
    ) => {
        // Authenticated-only user action.
        if (!isCurrent() || !requireAuth()) {
            return undefined;
        }

        try {
            const payload = buildSupportAttachmentPayload(replyData, replyData.attachment_files || []);
            const { data: newReply, error: apiError } = await addTicketReply(ticketNumber, payload);
            if (!isCurrent()) return undefined;

            if (apiError) {
                error.value = $t("support.messages.errorAddingReply");
                logSupportError('Failed to add reply:', apiError);
                throw apiError;
            }

            if (newReply) {
                // Update current ticket if it's the same - check for duplicates by created_at
                if (currentTicket.value?.ticket_number === ticketNumber && currentTicket.value.replies) {
                    const existingReplyIndex = currentTicket.value.replies.findIndex(r => r.created_at === newReply.created_at);
                    if (existingReplyIndex === -1) {
                        // Only add if it doesn't exist
                        currentTicket.value.replies.push(newReply);
                    }
                }

                // Update ticket in list - check for duplicates by created_at
                const ticketIndex = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
                if (ticketIndex !== -1 && tickets.value[ticketIndex].replies) {
                    const existingReplyIndex = tickets.value[ticketIndex].replies.findIndex(r => r.created_at === newReply.created_at);
                    if (existingReplyIndex === -1) {
                        // Only add if it doesn't exist
                        tickets.value[ticketIndex].replies.push(newReply);
                    }
                    tickets.value[ticketIndex].updated_at = newReply.created_at;
                }

                return newReply;
            }
        } catch (err) {
            if (!isCurrent()) return undefined;
            // Don't set global error - let component handle it locally
            logSupportError('Failed to add reply:', err);
            throw err;
        }
    };

    const createInternalCollaborationNote = async (ticketNumber: string, noteData: Api.Support.CreateInternalNoteData) => {
        try {
            const payload = buildSupportAttachmentPayload(noteData, noteData.attachment_files || []);
            const { data, error: apiError } = await createCollaborationInternalNote(ticketNumber, payload);

            if (apiError) {
                error.value = $t('support.messages.errorAddingReply');
                logSupportError('Failed to create collaboration internal note:', apiError);
                throw apiError;
            }

            if (data) {
                return mergeCollaborationMutation(ticketNumber, data);
            }
        } catch (err) {
            logSupportError('Failed to create collaboration internal note:', err);
            throw err;
        }
    };

    const updateCollaborationWatchers = async (ticketNumber: string, watcherAgentIds: string[]) => {
        try {
            const { data, error: apiError } = await updateTicketWatchers(ticketNumber, { watcher_agent_ids: watcherAgentIds });
            if (apiError) {
                logSupportError('Failed to update ticket watchers:', apiError);
                throw apiError;
            }
            return data ? mergeCollaborationMutation(ticketNumber, data) : undefined;
        } catch (err) {
            logSupportError('Failed to update ticket watchers:', err);
            throw err;
        }
    };

    const acknowledgeCollaborationAttention = async (ticketNumber: string, requestId: string | number) => {
        try {
            const { data, error: apiError } = await acknowledgeTicketAttentionRequest(ticketNumber, requestId);
            if (apiError) {
                logSupportError('Failed to acknowledge ticket attention request:', apiError);
                throw apiError;
            }
            return data ? mergeCollaborationMutation(ticketNumber, data) : undefined;
        } catch (err) {
            logSupportError('Failed to acknowledge ticket attention request:', err);
            throw err;
        }
    };

    const resolveCollaborationAttention = async (ticketNumber: string, requestId: string | number) => {
        try {
            const { data, error: apiError } = await resolveTicketAttentionRequest(ticketNumber, requestId);
            if (apiError) {
                logSupportError('Failed to resolve ticket attention request:', apiError);
                throw apiError;
            }
            return data ? mergeCollaborationMutation(ticketNumber, data) : undefined;
        } catch (err) {
            logSupportError('Failed to resolve ticket attention request:', err);
            throw err;
        }
    };

    const handoffTicketToAgent = async (ticketNumber: string, toAgentId: string, note?: string) => {
        try {
            const { data, error: apiError } = await handoffTicket(ticketNumber, { to_agent_id: toAgentId, note });
            if (apiError) {
                logSupportError('Failed to hand off ticket:', apiError);
                throw apiError;
            }
            return data ? mergeCollaborationMutation(ticketNumber, data) : undefined;
        } catch (err) {
            logSupportError('Failed to hand off ticket:', err);
            throw err;
        }
    };

    const updateTicketStatus = async (ticketNumber: string, status: string) => {
        try {
            const { data: updatedTicket, error: apiError } = await changeTicketStatus(ticketNumber, status);

            if (apiError) {
                error.value = $t('support.messages.errorUpdatingStatus');
                logSupportError('Failed to update ticket status:', apiError);
                throw apiError;
            }

            if (updatedTicket) {
                // Update current ticket with safe merge to preserve nested arrays
                if (currentTicket.value?.ticket_number === ticketNumber) {
                    currentTicket.value = mergeTicketWithExisting(currentTicket.value, updatedTicket);
                }

                let mergedListTicket: Api.Support.Ticket | undefined;

                // Update ticket in list with safe merge
                const index = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
                if (index !== -1) {
                    tickets.value[index] = mergeTicketWithExisting(tickets.value[index], updatedTicket);
                    mergedListTicket = tickets.value[index];
                }

                return currentTicket.value?.ticket_number === ticketNumber ? currentTicket.value : mergedListTicket;
            }
        } catch (err) {
            error.value = $t('support.messages.errorUpdatingStatus');
            logSupportError('Failed to update ticket status:', err);
            throw err;
        }
    };

    const updateTicketPriority = async (ticketNumber: string, priority: string) => {
        try {
            const { data: updatedTicket, error: apiError } = await changeTicketPriority(ticketNumber, priority);

            if (apiError) {
                error.value = $t('support.messages.errorUpdatingPriority');
                logSupportError('Failed to update ticket priority:', apiError);
                throw apiError;
            }

            if (updatedTicket) {
                // Update current ticket with safe merge to preserve nested arrays
                if (currentTicket.value?.ticket_number === ticketNumber) {
                    currentTicket.value = mergeTicketWithExisting(currentTicket.value, updatedTicket);
                }

                let mergedListTicket: Api.Support.Ticket | undefined;

                // Update ticket in list with safe merge
                const index = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
                if (index !== -1) {
                    tickets.value[index] = mergeTicketWithExisting(tickets.value[index], updatedTicket);
                    mergedListTicket = tickets.value[index];
                }

                return currentTicket.value?.ticket_number === ticketNumber ? currentTicket.value : mergedListTicket;
            }
        } catch (err) {
            error.value = $t('support.messages.errorUpdatingPriority');
            logSupportError('Failed to update ticket priority:', err);
            throw err;
        }
    };

    const assignTicketToAgent = async (ticketNumber: string, agentId: string | null) => {
        try {
            const { data: updatedTicket, error: apiError } = await assignTicket(ticketNumber, agentId);

            if (apiError) {
                error.value = $t('support.messages.errorAssigningTicket');
                logSupportError('Failed to assign ticket:', apiError);
                throw apiError;
            }

            if (updatedTicket) {
                // Update current ticket with safe merge to preserve nested arrays
                if (currentTicket.value?.ticket_number === ticketNumber) {
                    currentTicket.value = mergeTicketWithExisting(currentTicket.value, updatedTicket);
                }

                let mergedListTicket: Api.Support.Ticket | undefined;

                // Update ticket in list with safe merge
                const index = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
                if (index !== -1) {
                    tickets.value[index] = mergeTicketWithExisting(tickets.value[index], updatedTicket);
                    mergedListTicket = tickets.value[index];
                }

                return currentTicket.value?.ticket_number === ticketNumber ? currentTicket.value : mergedListTicket;
            }
        } catch (err) {
            error.value = $t('support.messages.errorAssigningTicket');
            logSupportError('Failed to assign ticket:', err);
            throw err;
        }
    };

    const closeTicketByNumber = async (ticketNumber: string) => {
        try {
            const { data: updatedTicket, error: apiError } = await closeTicket(ticketNumber);

            if (apiError) {
                error.value = $t('support.messages.errorClosingTicket');
                logSupportError('Failed to close ticket:', apiError);
                throw apiError;
            }

            if (updatedTicket) {
                // Update current ticket with safe merge to preserve nested arrays
                if (currentTicket.value?.ticket_number === ticketNumber) {
                    currentTicket.value = mergeTicketWithExisting(currentTicket.value, updatedTicket);
                }

                let mergedListTicket: Api.Support.Ticket | undefined;

                // Update ticket in list with safe merge
                const index = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
                if (index !== -1) {
                    tickets.value[index] = mergeTicketWithExisting(tickets.value[index], updatedTicket);
                    mergedListTicket = tickets.value[index];
                }

                return currentTicket.value?.ticket_number === ticketNumber ? currentTicket.value : mergedListTicket;
            }
        } catch (err) {
            error.value = $t('support.messages.errorClosingTicket');
            logSupportError('Failed to close ticket:', err);
            throw err;
        }
    };

    const reopenTicketByNumber = async (ticketNumber: string) => {
        try {
            const { data: updatedTicket, error: apiError } = await reopenTicket(ticketNumber);

            if (apiError) {
                error.value = $t('support.messages.errorReopeningTicket');
                logSupportError('Failed to reopen ticket:', apiError);
                throw apiError;
            }

            if (updatedTicket) {
                // Update current ticket with safe merge to preserve nested arrays
                if (currentTicket.value?.ticket_number === ticketNumber) {
                    currentTicket.value = mergeTicketWithExisting(currentTicket.value, updatedTicket);
                }

                let mergedListTicket: Api.Support.Ticket | undefined;

                // Update ticket in list with safe merge
                const index = tickets.value.findIndex(t => t.ticket_number === ticketNumber);
                if (index !== -1) {
                    tickets.value[index] = mergeTicketWithExisting(tickets.value[index], updatedTicket);
                    mergedListTicket = tickets.value[index];
                }

                return currentTicket.value?.ticket_number === ticketNumber ? currentTicket.value : mergedListTicket;
            }
        } catch (err) {
            error.value = $t('support.messages.errorReopeningTicket');
            logSupportError('Failed to reopen ticket:', err);
            throw err;
        }
    };

    const searchTicketsQuery = async (query: string) => {
        if (!query.trim()) {
            searchResults.value = [];
            return;
        }

        // Authenticated-only: guests get a throttled toast and empty results.
        if (!requireAuth()) {
            searchResults.value = [];
            return;
        }

        searching.value = true;
        try {
            const { data: response, error: apiError } = await searchTickets(query, filters.value);

            if (apiError) {
                logSupportError('Failed to search tickets:', apiError);
                searchResults.value = [];
                return;
            }

            if (response) {
                searchResults.value = response.results;
            }
        } catch (err) {
            logSupportError('Failed to search tickets:', err);
            searchResults.value = [];
        } finally {
            searching.value = false;
        }
    };

    const getDashboardStats = async () => {
        // Authenticated-only: guests get a throttled toast and no stats
        // (non-error state; the LoginOverlay already covers the view).
        if (!requireAuth()) {
            dashboardStatsError.value = null;
            return;
        }

        dashboardStatsError.value = null;
        try {
            const { data: stats, error: apiError } = await fetchDashboardStats();

            if (apiError) {
                logSupportError('Failed to fetch dashboard stats:', apiError);
                dashboardStatsError.value = $t('support.messages.statisticsUnavailable');
                return;
            }

            if (stats) {
                dashboardStats.value = stats;
                dashboardStatsError.value = null;
            }
        } catch (err) {
            logSupportError('Failed to fetch dashboard stats:', err);
            dashboardStatsError.value = $t('support.messages.statisticsUnavailable');
        }
    };

    const updateFilters = (newFilters: Partial<Api.Support.TicketFilters>) => {
        filters.value = { ...filters.value, ...newFilters };
        pagination.value.page = 1; // Reset to first page
    };

    const clearFilters = () => {
        filters.value = {};
        searchQuery.value = '';
        pagination.value.page = 1;
    };

    const setCurrentUserId = (userId: string) => {
        currentUserId.value = userId;
    };

    const updatePagination = (page: number, pageSize?: number) => {
        pagination.value.page = page;
        if (pageSize) {
            pagination.value.pageSize = pageSize;
        }
    };

    const refreshTickets = async () => {
        await getTickets();
    };

    const refreshCurrentTicket = async () => {
        if (currentTicket.value) {
            await getTicketDetails(currentTicket.value.ticket_number);
        }
    };

    // Polling synchronization methods
    const registerPollingInstance = (instanceId: string) => {
        pollingState.value.pollingInstances.add(instanceId);
        pollingState.value.isPolling = pollingState.value.pollingInstances.size > 0;
    };

    const unregisterPollingInstance = (instanceId: string) => {
        pollingState.value.pollingInstances.delete(instanceId);
        pollingState.value.isPolling = pollingState.value.pollingInstances.size > 0;
    };

    const processPollingUpdate = (update: PollingUpdate) => {
        // Prevent processing stale updates
        const now = Date.now();
        if (now - update.timestamp > 30000) { // Ignore updates older than 30 seconds
            console.warn('[SupportStore] Ignoring stale polling update:', {
                type: update.type,
                source: update.source,
                timestamp: update.timestamp
            });
            return false;
        }

        // Update last update timestamp
        pollingState.value.lastUpdate = now;


        switch (update.type) {
            case 'tickets':
                if (update.data && Array.isArray(update.data.results)) {
                    // Smart merge: only update if data is newer or different
                    const newTickets = update.data.results.map((ticket: Api.Support.Ticket) => mergeSupportTicketListItem(
                        tickets.value.find(existing => existing.ticket_number === ticket.ticket_number), ticket, hasAgentAccess.value
                    ));
                    const hasChanges = !areTicketsEqual(tickets.value, newTickets);

                    if (hasChanges) {
                        tickets.value = newTickets;
                    }
                    pagination.value.total = update.data.count ?? newTickets.length;
                }
                break;

            case 'ticket-details':
                if (update.data && currentTicket.value && update.data.ticket_number === currentTicket.value.ticket_number) {
                    // Only update if the ticket data has actually changed
                    if (!areTicketsEqual([currentTicket.value], [update.data])) {
                        currentTicket.value = update.data;

                        // Also update in tickets list if present.
                        // Merge to preserve list-only fields (unread_state,
                        // replies_count) that detail payloads don't carry.
                        // A newer list event remains unread until it is rendered.
                        const index = tickets.value.findIndex(t => t.ticket_number === update.data.ticket_number);
                        if (index !== -1) {
                            const existingListTicket = tickets.value[index];
                            tickets.value[index] = mergeSupportTicketDetail(existingListTicket, update.data, hasAgentAccess.value);
                        }
                    }
                }
                break;

            case 'dashboard-stats':
                if (update.data) {
                    const hasChanges = !areStatsEqual(dashboardStats.value, update.data);
                    if (hasChanges) {
                        dashboardStats.value = update.data;
                    }
                }
                break;

            default:
                console.warn(`[SupportStore] Unknown polling update type:`, update.type);
                return false;
        }

        return true;
    };

    const queuePollingUpdate = (update: PollingUpdate) => {
        // Add to queue for batch processing
        pollingState.value.updateQueue.push(update);

        // Process queue with debouncing to prevent excessive updates
        processUpdateQueue();
    };

    let updateQueueTimer: NodeJS.Timeout | null = null;
    const processUpdateQueue = () => {
        if (updateQueueTimer) {
            clearTimeout(updateQueueTimer);
        }

        updateQueueTimer = setTimeout(() => {
            const queue = pollingState.value.updateQueue.splice(0);
            if (queue.length === 0) return;


            // Group updates by type and process the most recent for each type
            const latestUpdates = new Map<string, PollingUpdate>();
            queue.forEach(update => {
                const existing = latestUpdates.get(update.type);
                if (!existing || update.timestamp > existing.timestamp) {
                    latestUpdates.set(update.type, update);
                }
            });

            // Process the latest updates
            latestUpdates.forEach(update => {
                processPollingUpdate(update);
            });

            updateQueueTimer = null;
        }, 100); // 100ms debounce
    };

    // Helper functions for data comparison
    const areTicketsEqual = (tickets1: Api.Support.Ticket[], tickets2: Api.Support.Ticket[]): boolean => {
        if (tickets1.length !== tickets2.length) return false;

        return tickets1.every((ticket1, index) => {
            const ticket2 = tickets2[index];
            return ticket1.ticket_number === ticket2.ticket_number &&
                ticket1.updated_at === ticket2.updated_at &&
                ticket1.status === ticket2.status &&
                ticket1.priority === ticket2.priority &&
                JSON.stringify(ticket1.latest_visible_reply) === JSON.stringify(ticket2.latest_visible_reply) &&
                JSON.stringify(ticket1.unread_state) === JSON.stringify(ticket2.unread_state) &&
                ticket1.replies?.length === ticket2.replies?.length;
        });
    };

    const SUPPORT_DASHBOARD_STAT_KEYS = [
        'totalTickets',
        'openTickets',
        'inProgressTickets',
        'waitingCustomerTickets',
        'resolvedTickets',
        'closedTickets',
        'unassignedTickets',
        'overdueTickets',
        'awaitingAgentTickets',
        'neverAgentAnsweredTickets',
        'waitingUnder12hTickets',
        'waiting12To24hTickets',
        'waitingOver24hTickets',
        'avgResponseTime',
        'avgResolutionTime'
    ] as const;

    const SUPPORT_DASHBOARD_MY_TICKET_KEYS = [
        'total',
        'open',
        'in_progress',
        'waiting_customer',
        'waiting_for_agent',
        'resolved',
        'closed'
    ] as const;

    const areMyTicketStatsEqual = (
        stats1?: Api.Support.DashboardStats['my_tickets'],
        stats2?: Api.Support.DashboardStats['my_tickets']
    ): boolean => {
        if (!stats1 && !stats2) return true;
        if (!stats1 || !stats2) return false;

        return SUPPORT_DASHBOARD_MY_TICKET_KEYS.every(key => stats1[key] === stats2[key]);
    };

    const areStatsEqual = (stats1: Api.Support.DashboardStats | null, stats2: Api.Support.DashboardStats): boolean => {
        if (!stats1) return false;

        return SUPPORT_DASHBOARD_STAT_KEYS.every(key => stats1[key] === stats2[key]) &&
            areMyTicketStatsEqual(stats1.my_tickets, stats2.my_tickets);
    };

    // Polling-aware refresh methods
    const refreshTicketsFromPolling = async (source = 'polling') => {
        // Never poll for guests; silent (background) so no toast.
        if (!requireAuth({ silent: true })) return;

        const update: PollingUpdate = {
            type: 'tickets',
            data: null,
            timestamp: Date.now(),
            source
        };

        try {
            const { data, error: apiError, statusCode } = await fetchTickets({
                ...filters.value,
                page: pagination.value.page,
                page_size: pagination.value.pageSize
            });

            // Handle 429 errors gracefully for polling
            if (statusCode === 429) {
                return;
            }

            if (!apiError && data) {
                update.data = data;
                queuePollingUpdate(update);
            }
        } catch (err: any) {
            // Handle 429 errors gracefully for polling (fallback for unexpected errors)
            const errStatusCode = err?.statusCode || err?.response?.status;
            if (errStatusCode === 429 || err?.rateLimitInfo) {
                // Don't log as error, this is expected behavior
                return;
            }
            logSupportError('[SupportStore] Failed to refresh tickets from polling:', err);
        }
    };

    const refreshCurrentTicketFromPolling = async (source = 'polling') => {
        // Never poll for guests; silent (background) so no toast.
        if (!requireAuth({ silent: true })) return;

        if (!currentTicket.value) return;

        const update: PollingUpdate = {
            type: 'ticket-details',
            data: null,
            timestamp: Date.now(),
            source
        };

        try {
            const { data, error: apiError, statusCode } = await fetchTicketDetails(currentTicket.value.ticket_number);

            // Handle 429 errors gracefully for polling
            if (statusCode === 429) {
                return;
            }

            if (!apiError && data) {
                update.data = data;
                queuePollingUpdate(update);
            }
        } catch (err: any) {
            // Handle 429 errors gracefully for polling (fallback for unexpected errors)
            const errStatusCode = err?.statusCode || err?.response?.status;
            if (errStatusCode === 429 || err?.rateLimitInfo) {
                // Don't log as error, this is expected behavior
                return;
            }
            logSupportError('[SupportStore] Failed to refresh current ticket from polling:', err);
        }
    };

    const refreshDashboardStatsFromPolling = async (source = 'polling') => {
        // Never poll for guests; silent (background) so no toast.
        if (!requireAuth({ silent: true })) return;

        const update: PollingUpdate = {
            type: 'dashboard-stats',
            data: null,
            timestamp: Date.now(),
            source
        };

        try {
            const { data, error: apiError, statusCode } = await fetchDashboardStats();

            // Handle 429 errors gracefully for polling
            if (statusCode === 429) {
                return;
            }

            if (!apiError && data) {
                update.data = data;
                queuePollingUpdate(update);
            }
        } catch (err: any) {
            // Handle 429 errors gracefully for polling (fallback for unexpected errors)
            const errStatusCode = err?.statusCode || err?.response?.status;
            if (errStatusCode === 429 || err?.rateLimitInfo) {
                // Don't log as error, this is expected behavior
                return;
            }
            logSupportError('[SupportStore] Failed to refresh dashboard stats from polling:', err);
        }
    };

    // Computed properties for polling state
    const isActivelyPolling = computed(() => pollingState.value.isPolling);
    const activePollingInstances = computed(() => pollingState.value.pollingInstances.size);
    const lastPollingUpdate = computed(() => pollingState.value.lastUpdate);

    // Initialize
    const initialize = async () => {
        await getDashboardStats();
    };

    return {
        // State
        tickets,
        currentTicket,
        dashboardStats,
        loading,
        error,
        errorCode,
        filters,
        pagination,
        searchQuery,
        searchResults,
        searching,

        // User profile and roles state
        userProfile,
        userProfileLoading,
        userProfileLoaded,
        userProfileError,
        dashboardStatsError,

        // Agent-related state
        agents,
        agentsLoading,
        agentsLoaded,

        // Canned responses state
        cannedResponses,
        cannedResponsesLoading,
        cannedResponsesLoaded,
        replyTemplates,
        replyTemplatesLoading,
        replyTemplatesLoaded,

        // Categories cache
        categories,
        categoriesLoading,
        categoriesLoaded,

        // Polling state
        pollingState,
        isActivelyPolling,
        activePollingInstances,
        lastPollingUpdate,

        // Computed
        openTickets,
        inProgressTickets,
        resolvedTickets,
        overdueTickets,
        myTickets,
        filteredTickets,

        // Role-based computed properties
        isAgent,
        isAdmin,
        hasAgentAccess,
        hasSupportAccess,
        hasSupportAIOperationsAccess,
        hasSupportAIReplayAccess,
        hasSupportAIReleaseRequestAccess,
        hasSupportAICustomerReadinessAccess,
        hasSupportAIRolloutControlAccess,
        hasSupportAICalibrationReviewAccess,
        hasSupportAICalibrationManageAccess,
        hasSupportAICalibrationApproveAccess,
        assignedToMeTickets,
        unassignedTickets,

        // Actions
        getTickets,
        invalidateSupportAIOperationsAccess,
        recheckUserProfilePreservingCachedAccess,
        recheckStaffAIAccess,
        getTicketDetails,
        createNewTicket,
        addReply,
        createInternalCollaborationNote,
        updateCollaborationWatchers,
        acknowledgeCollaborationAttention,
        resolveCollaborationAttention,
        handoffTicketToAgent,
        updateTicketStatus,
        updateTicketPriority,
        assignTicketToAgent,
        closeTicketByNumber,
        reopenTicketByNumber,
        searchTicketsQuery,
        getDashboardStats,
        updateFilters,
        clearFilters,
        setCurrentUserId,
        updatePagination,
        refreshTickets,
        refreshCurrentTicket,
        initialize,

        // User profile and role actions
        getUserProfile,
        getAgents,
        getCannedResponses,
        recordCannedResponseUse,
        getReplyTemplates,

        // Reference data actions
        getCategories,

        // Polling synchronization
        registerPollingInstance,
        unregisterPollingInstance,
        processPollingUpdate,
        queuePollingUpdate,
        refreshTicketsFromPolling,
        refreshCurrentTicketFromPolling,
        refreshDashboardStatsFromPolling
    };
});
